import React from 'react';
import { Box, Stack, Typography, Tooltip, Chip } from '@mui/material';
import { NameEmailAvatar } from '../../../../../ui';

const TooltipWrapper: React.FC<{ text: string; children?: React.ReactNode }> = ({
  text,
  children,
}) => {
  return (
    <Tooltip title={text} arrow placement="top">
      <Box
        sx={{
          maxWidth: 180,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'help',
        }}
      >
        {children || text || '-'}
      </Box>
    </Tooltip>
  );
};

// Format timestamp to readable date
const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format duration in minutes to readable format
const formatDuration = (minutes: number): string => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${remainingMinutes}m`;
};

// Get assessment type badge color and styling
const getAssessmentTypeBadge = (type: string) => {
  const typeMap: Record<string, { color: 'primary' | 'success' | 'secondary' | 'default' }> = {
    candidate: { color: 'primary' },
    employee: { color: 'success' },
    partner: { color: 'secondary' },
    default: { color: 'default' },
  };

  const badgeProps = typeMap[type] || typeMap.default;

  return (
    <Chip
      label={type || 'Unknown'}
      color={badgeProps.color}
      size="small"
      variant="outlined"
      sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 11 }}
    />
  );
};

// Get status badge
const getStatusBadge = (isActive: boolean) => {
  return (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      color={isActive ? 'success' : 'error'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        fontSize: 11,
        '& .MuiChip-icon': { ml: 0.5 },
      }}
      icon={
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: isActive ? 'success.main' : 'error.main',
          }}
        />
      }
    />
  );
};

// Get pre-screen badge
const getPreScreenBadge = (preScreen: any[]) => {
  const hasPreScreen = preScreen && preScreen.length > 0;

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Chip
        label={hasPreScreen ? 'Enabled' : 'Disabled'}
        color={hasPreScreen ? 'success' : 'default'}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: 11,
          '& .MuiChip-icon': { ml: 0.5 },
        }}
        icon={
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: hasPreScreen ? 'success.main' : 'text.disabled',
            }}
          />
        }
      />
      {hasPreScreen && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
          ({preScreen.length} questions)
        </Typography>
      )}
    </Stack>
  );
};

export const getColumns = () => {
  return [
    {
      key: 'id',
      label: 'ID',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => row.id,
      disableSort: true,
    },
    {
      key: 'assessmentTitle',
      label: 'Assessment Title',
      width: '250px',
      align: 'left' as const,
      render: (row: any) => (
        <Stack spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.assessmentTitle || 'Untitled Assessment'}
          </Typography>
          {row.description && (
            <TooltipWrapper text={row.description}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {row.description}
              </Typography>
            </TooltipWrapper>
          )}
        </Stack>
      ),
      disableSort: false,
    },
    {
      key: 'assessmentType',
      label: 'Type',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => getAssessmentTypeBadge(row.assessmentType),
      disableSort: false,
    },
    {
      key: 'user',
      label: 'Creator',
      width: '200px',
      align: 'left' as const,
      render: (row: any) => (
        <NameEmailAvatar
          email={row.user?.email || 'N/A'}
          name={
            `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim() ||
            'Unknown User'
          }
          avatar={row.user?.profilePic}
        />
      ),
      disableSort: false,
    },
    {
      key: 'jobPosition',
      label: 'Job Position',
      width: '250px',
      align: 'left' as const,
      render: (row: any) => (
        <Stack spacing={0.25}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.jobPosition || 'Not Specified'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {row.totalScore ? `${row.totalScore} points` : 'No scoring'}
          </Typography>
        </Stack>
      ),
      disableSort: false,
    },
    {
      key: 'totalTime',
      label: 'Duration',
      width: '100px',
      align: 'left' as const,
      render: (row: any) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            {formatDuration(row.totalTime)}
          </Typography>
          {row.isStrictDuration && (
            <Chip
              label="Strict"
              size="small"
              color="warning"
              sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
            />
          )}
        </Stack>
      ),
      disableSort: false,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {formatDate(row.startDate)}
        </Typography>
      ),
      disableSort: false,
    },
    {
      key: 'endDate',
      label: 'End Date',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {formatDate(row.endDate)}
        </Typography>
      ),
      disableSort: false,
    },
    {
      key: 'creationDateTime',
      label: 'Created',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {formatDate(row.creationDateTime)}
        </Typography>
      ),
      disableSort: false,
    },
    {
      key: 'preScreen',
      label: 'Pre-Screen',
      width: '130px',
      align: 'left' as const,
      render: (row: any) => getPreScreenBadge(row.preScreen),
      disableSort: false,
    },
    {
      key: 'isActive',
      label: 'Status',
      width: '100px',
      align: 'left' as const,
      render: (row: any) => getStatusBadge(row.isActive),
      disableSort: false,
    },
  ];
};
